import React from 'react';
import { Box } from '@mui/material';
import { AppThemeProvider } from './styles/ThemeContext';
import Sidebar from './components/Sidebar';
import TopBar from './components/TopBar';
import LoginPage from './pages/Login/LoginPage';
import OperatingNodes from './pages/node/OperatingNodes';
import Guards from './pages/guard/Guards';
import CommandCentre from './pages/commandCentre/CommandCentre';
import CreateAccount from './pages/commandCentre/CreateAccount';
import CommandCentreDetail from './pages/commandCentre/CommandCentreDetail';
import AddGuard from './pages/guard/AddGuard';
import CreateNode from './pages/node/CreateNode';
import LEStations from './pages/leStations/LEStations';
import CreateStation from './pages/leStations/CreateStation';
import AuditLogs from './pages/auditLogs/AuditLogs';
import AuditLogDetail from './pages/auditLogs/AuditLogDetail';
import MyAccount from './pages/myAccount/MyAccount';
import Geography from './pages/geography/Geography';
import { useState } from 'react';
import { logOut, getStoredToken, subscribeToAuthChanges } from './services/authService';


function App() {
  const [page, setPage] = useState<'list' | 'create' | 'guards' | 'command' | 'commandCreate' | 'commandDetail' | 'leStations' | 'leStationsCreate' | 'audit' | 'auditDetail' | 'myAccount' | 'geography'>('list');
  const [isAuthenticated, setIsAuthenticated] = useState<boolean>(() => !!getStoredToken());
  const [guardsSubpage, setGuardsSubpage] = useState<'list' | 'add'>('list');
  const [sidebarSelection, setSidebarSelection] = useState<string>('Operating Nodes');
  const [notification, setNotification] = useState<{ show: boolean; message?: string; severity?: 'success' | 'error' }>({ show: false });
  const [selectedNode, setSelectedNode] = useState<any | undefined>(undefined);
  const [selectedGuard, setSelectedGuard] = useState<any | undefined>(undefined);
  const [selectedCentral, setSelectedCentral] = useState<any | undefined>(undefined);
  const [selectedLEStation, setSelectedLEStation] = useState<any | undefined>(undefined);
  const [auditDetail, setAuditDetail] = useState<any | undefined>(undefined);

  // subscribe to Firebase auth token changes for auto-logout on session expiry
  React.useEffect(() => {
    const unsubscribe = subscribeToAuthChanges(
      () => { /* token silently refreshed — nothing to do */ },
      () => {
        // session expired or revoked — force logout
        setIsAuthenticated(false);
        setPage('list');
        setSidebarSelection('Operating Nodes');
      },
    );
    return unsubscribe;
  }, []);

  // handle 401 responses from any API call
  React.useEffect(() => {
    const onUnauthorized = () => {
      logOut().finally(() => {
        setIsAuthenticated(false);
        setPage('list');
        setSidebarSelection('Operating Nodes');
      });
    };
    window.addEventListener('wualt:auth:unauthorized', onUnauthorized);
    return () => window.removeEventListener('wualt:auth:unauthorized', onUnauthorized);
  }, []);

  // listen for dispatched notifications from child modules
  React.useEffect(() => {
    let suppressErrors = false;
    const onUnauthorizedSuppressor = () => { suppressErrors = true; };
    window.addEventListener('wualt:auth:unauthorized', onUnauthorizedSuppressor);

    const onSuccess = (ev: Event) => {
      const msg = (ev as CustomEvent)?.detail?.message ?? '';
      setNotification({ show: true, message: msg, severity: 'success' });
      setTimeout(() => setNotification({ show: false }), 3500);
    };
    const onError = (ev: Event) => {
      if (suppressErrors) return;
      const msg = (ev as CustomEvent)?.detail?.message ?? '';
      setNotification({ show: true, message: msg, severity: 'error' });
      setTimeout(() => setNotification({ show: false }), 3500);
    };
    window.addEventListener('wualt:notification', onSuccess as EventListener);
    window.addEventListener('wualt:notification:error', onError as EventListener);
    return () => {
      window.removeEventListener('wualt:auth:unauthorized', onUnauthorizedSuppressor);
      window.removeEventListener('wualt:notification', onSuccess as EventListener);
      window.removeEventListener('wualt:notification:error', onError as EventListener);
    };
  }, []);

  // App only handles navigation/notification after a node is created
  const handleSavedNotification = (message = 'Node created successfully') => {
    setNotification({ show: true, message });
    setPage('list');
    // auto-hide after 3.5s
    setTimeout(() => setNotification({ show: false }), 3500);
  };

  // logout signs out from Firebase and clears stored credentials
  const handleLogout = () => {
    logOut().finally(() => {
      setIsAuthenticated(false);
      setPage('list');
      setSidebarSelection('Operating Nodes');
    });
  };
  return (
    <AppThemeProvider>
      {!isAuthenticated ? (
        <LoginPage onLogin={() => setIsAuthenticated(true)} />
      ) : (
      <Box display="flex">
  <Sidebar selected={sidebarSelection} onNavigate={(label) => { setSidebarSelection(label); setGuardsSubpage('list'); if (label === 'Guards') setPage('guards'); else if (label === 'Central Command') setPage('command'); else if (label === 'LE Stations') setPage('leStations'); else if (label === 'Geography') setPage('geography'); else if (label === 'Audit Log') setPage('audit'); else if (label === 'My Account') setPage('myAccount'); else setPage('list'); }} />
  <TopBar onMyAccount={() => setPage('myAccount')} onLogout={handleLogout} />
  <Box component="main" flex={1} pt="56px">
            {page === 'list' ? (
            <OperatingNodes onCreate={() => { setSelectedNode(undefined); setPage('create'); }} onEdit={(node) => { setSelectedNode(node); setPage('create'); }} notification={notification} />
          ) : page === 'create' ? (
            <CreateNode initialNode={selectedNode} onCancel={() => setPage('list')} onSaved={(msg?: string) => { setPage('list'); setNotification({ show: true, message: msg ?? (selectedNode ? 'Node updated successfully' : 'Node created successfully'), severity: 'success' }); setTimeout(() => setNotification({ show: false }), 3500); }} onDeactivate={(nodeName: string) => { setPage('list'); setNotification({ show: true, message: `Node ${nodeName} deactivated`, severity: 'success' }); setTimeout(() => setNotification({ show: false }), 3500); }} />
          ) : page === 'command' ? (
                <CommandCentre onEdit={(c) => { setSelectedCentral(c); setPage('commandDetail'); }} notification={notification} />
          ) : page === 'commandDetail' ? (
            <CommandCentreDetail commandCentre={selectedCentral} onBack={() => setPage('command')} onUpdated={(msg?: string) => { setNotification({ show: true, message: msg ?? 'Command Centre updated', severity: 'success' }); setTimeout(() => setNotification({ show: false }), 3500); }} />
          ) : page === 'commandCreate' ? (
            <CreateAccount initial={selectedCentral} onCancel={() => setPage('command')} onSave={() => { setPage('command'); setNotification({ show: true, message: selectedCentral ? 'Account updated successfully' : 'Account created successfully', severity: 'success' }); setTimeout(() => setNotification({ show: false }), 3500); }} onDeactivate={(name: string) => { setPage('command'); setNotification({ show: true, message: `Account ${name} deactivated`, severity: 'success' }); setTimeout(() => setNotification({ show: false }), 3500); }} />
          ) : page === 'leStations' ? (
            <LEStations onAdd={() => { setSelectedLEStation(undefined); setPage('leStationsCreate'); }} onEdit={(r) => { setSelectedLEStation(r); setPage('leStationsCreate'); }} onImported={(msg?: string) => { setNotification({ show: true, message: msg ?? 'LE Stations imported', severity: 'success' }); setTimeout(() => setNotification({ show: false }), 3500); }} notification={notification} />
          ) : page === 'leStationsCreate' ? (
            <CreateStation initialData={selectedLEStation} onCancel={() => setPage('leStations')} onSave={(data) => { setPage('leStations'); setNotification({ show: true, message: selectedLEStation ? 'Station updated successfully' : 'Station created successfully', severity: 'success' }); setTimeout(() => setNotification({ show: false }), 3500); }} />
            ) : page === 'guards' ? (
            guardsSubpage === 'list' ? (
              <Guards onAdd={() => { setSelectedGuard(undefined); setGuardsSubpage('add'); }} onEdit={(g) => { setSelectedGuard(g); setGuardsSubpage('add'); }} onImported={(msg?: string) => {
                setGuardsSubpage('list');
                setNotification({ show: true, message: msg ?? 'Guards imported', severity: 'success' });
                setTimeout(() => setNotification({ show: false }), 3500);
              }} notification={notification} />
            ) : (
              <AddGuard initialGuard={selectedGuard} onCancel={() => setGuardsSubpage('list')} onSave={(msg?: string) => {
                setGuardsSubpage('list');
                // prefer backend-provided message when available
                if (msg) {
                  setNotification({ show: true, message: msg, severity: 'success' });
                } else if (selectedGuard) {
                  setNotification({ show: true, message: 'Guard updated successfully', severity: 'success' });
                } else {
                  setNotification({ show: true, message: 'Guard created successfully', severity: 'success' });
                }
                setTimeout(() => setNotification({ show: false }), 3500);
              }} onDeactivate={(name: string) => {
                // handle guard deactivate locally
                console.log('Guard deactivate requested for', name);
                setGuardsSubpage('list');
                setNotification({ show: true, message: `Guard ${name} deactivated`, severity: 'success' });
                setTimeout(() => setNotification({ show: false }), 3500);
              }} />
            )
          ) : page === 'audit' ? (
            <AuditLogs notification={notification} onOpenDetail={(row) => { setAuditDetail(row); setPage('auditDetail'); }} />
          ) : page === 'auditDetail' ? (
            <AuditLogDetail audit={auditDetail} onBack={() => setPage('audit')} />
          ) : page === 'myAccount' ? (
            <MyAccount />
          ) : page === 'geography' ? (
            <Geography notification={notification} />
          ) : null}
        </Box>
      </Box>
      )}
    </AppThemeProvider>
  );

}

export default App;
